import { Link, Navigate, useParams } from "@/lib/router-compat";
import { ArrowLeft, ArrowRight, Calendar, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import SEO from "@/components/SEO";
import FadeIn from "@/components/FadeIn";
import PricingEstimator from "@/components/PricingEstimator";
import ArticleRenderer from "@/components/ArticleRenderer";
import PageHero from "@/components/PageHero";
import { useSlotPhoto } from "@/lib/slotPhotos";
import { useRecordUsage } from "@/lib/mediaUsage";
import PhotoSlider from "@/components/PhotoSlider";
import SeoRelatedLinks from "@/components/SeoRelatedLinks";
import { articleServiceLinks } from "@/lib/seoInternalLinks";

import { articles, getArticleBySlug, type ArticleBlock } from "@/data/articles";

const SITE = "https://modernfenceinstall.com";

const LearnArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  // Slot-managed body image (per article). Hooks run unconditionally; safe-noop when no slug.
  const bodySlotKey = `article-body:${slug ?? ""}`;
  const { data: bodyPhoto } = useSlotPhoto(bodySlotKey);
  useRecordUsage([bodyPhoto?.id], `/learn/${slug ?? ""}`, `Article: ${slug ?? ""}`, "article-body");

  if (!article) return <Navigate to="/learn" replace />;

  // Auto-inject one inline image after the second h2 if the article body has none.
  // Falls back to the article's own hero image so every article has at least one in-content image.
  const renderedBody: ArticleBlock[] = (() => {
    const hasImage = article.body.some((b) => b.type === "image");
    if (hasImage) return article.body;
    const src = bodyPhoto?.public_url ?? article.heroImage;
    const alt = bodyPhoto?.alt_text ?? article.heroAlt;
    const imageBlock: ArticleBlock = {
      type: "image",
      src,
      alt,
    };
    let h2Count = 0;
    let injected = false;
    const next: ArticleBlock[] = [];
    for (const block of article.body) {
      next.push(block);
      if (!injected && block.type === "h2") {
        h2Count += 1;
        if (h2Count === 2) {
          next.push(imageBlock);
          injected = true;
        }
      }
    }
    // If the article has fewer than 2 h2s, put the image after the first paragraph.
    if (!injected) {
      const firstParaIdx = next.findIndex((b) => b.type === "p");
      if (firstParaIdx >= 0) {
        next.splice(firstParaIdx + 1, 0, imageBlock);
      } else {
        next.push(imageBlock);
      }
    }
    return next;
  })();

  const canonical = `${SITE}/learn/${article.slug}`;
  const ogImage = `${SITE}/og-default.jpg`;

  // Pick 2 sibling articles (same category if possible)
  const siblings = articles
    .filter((a) => a.slug !== article.slug)
    .sort((a, b) =>
      a.category === article.category && b.category !== article.category ? -1 : 1,
    )
    .slice(0, 2);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.heroImage.startsWith("https://") ? article.heroImage : `${SITE}${article.heroImage}`,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: { "@id": `${SITE}/#organization` },
    publisher: { "@id": `${SITE}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const faqJsonLd =
    article.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Learn", item: `${SITE}/learn` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonical },
    ],
  };

  const jsonLd = faqJsonLd
    ? [articleJsonLd, faqJsonLd, breadcrumbJsonLd]
    : [articleJsonLd, breadcrumbJsonLd];

  const ctaServiceLabel: Record<NonNullable<typeof article.ctaService>, string> = {
    "dog-fence": "Dog Fence Installation",
    "deer-fence": "Deer Fence Installation",
    "garden-fence": "Garden Fence Installation",
    "fence-restoration": "Fence Restoration",
  };

  return (
    <>
      <SEO
        title={`${article.seoTitle ?? article.title} | Modern Fence Install`}
        description={article.description}
        canonical={canonical}
        ogType="article"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <PageHero
        media="image"
        imageSrc={article.heroImage}
        imageAlt={article.heroAlt}
        align="left"
        overlay="strong"
        eyebrow={article.category}
        title={article.title}
        subtitle={article.description}
        topSlot={
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 text-mesh hover:text-charcoal-foreground text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
        }
      >
        <div className="flex flex-wrap items-center gap-4 text-sm text-mesh">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            Updated {new Date(article.publishDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {article.readingMinutes} min read
          </span>
        </div>
      </PageHero>

      {/* Breadcrumbs */}
      <div className="bg-offwhite border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/learn">Learn</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="line-clamp-1">{article.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Article body */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-10 max-w-6xl mx-auto">
            <article>
              <ArticleRenderer blocks={renderedBody} />

              {/* End-of-article CTA */}
              {article.ctaService && (
                <div className="mt-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-7 sm:p-8">
                  <p className="text-primary-foreground/85 text-sm font-semibold uppercase tracking-widest mb-2">
                    Ready to talk specifics?
                  </p>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-3">
                    Get a custom {ctaServiceLabel[article.ctaService].toLowerCase()} quote
                  </h3>
                  <p className="text-primary-foreground/90 mb-5 max-w-xl">
                    Free virtual consultation, no pressure, custom recommendation for your property.
                    Your install timeline is confirmed in your written proposal.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button size="lg" variant="cta" className="font-semibold gap-2" asChild>
                      <Link to="/book">
                        Book Free Consultation <ArrowRight className="w-5 h-5" />
                      </Link>
                    </Button>
                    <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground gap-2" asChild>
                      <Link to={`/services/${article.ctaService}`}>
                        Learn about {ctaServiceLabel[article.ctaService].toLowerCase()} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              )}

              {/* FAQ */}
              {article.faqs.length > 0 && (
                <div className="mt-14">
                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-5">
                    Frequently Asked Questions
                  </h2>
                  <Accordion type="single" collapsible className="space-y-3">
                    {article.faqs.map((f, i) => (
                      <AccordionItem
                        key={i}
                        value={`f-${i}`}
                        className="bg-offwhite rounded-lg px-6 border border-border shadow-xs"
                      >
                        <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-5">
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                          {f.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Related */}
              {article.related && article.related.length > 0 && (
                <div className="mt-14 pt-8 border-t border-border">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                    Keep reading
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {article.related.map((r) => (
                      <Link
                        key={r.href}
                        to={r.href}
                        className="inline-flex items-center gap-1 px-4 py-2 bg-offwhite border border-border rounded-full text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
                      >
                        {r.label} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Sidebar */}
            <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
              <SeoRelatedLinks heading="Related fence services" links={articleServiceLinks(article.title, article.category)} />
              <div className="bg-primary/5 rounded-xl border border-primary/20 p-6">
                <p className="font-semibold text-foreground mb-2">Ready to talk?</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Get a tailored estimate from our team — free, no obligation.
                </p>
                <Button asChild className="w-full gap-2">
                  <Link to="/book">
                    Book free consultation <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>

              <div className="bg-offwhite rounded-xl border border-border p-5">
                <p className="font-semibold text-foreground mb-2">Prefer to talk?</p>
                <a
                  href="tel:+18884163144"
                  className="inline-flex items-center gap-2 text-primary font-semibold"
                >
                  <Phone className="w-4 h-4" /> (888) 416-3144
                </a>
                <p className="text-xs text-muted-foreground mt-2">
                  We typically reply within one business day.
                </p>
              </div>

              {siblings.length > 0 && (
                <div className="bg-offwhite rounded-xl border border-border p-5">
                  <p className="font-semibold text-foreground mb-3">More from Learn</p>
                  <ul className="space-y-3">
                    {siblings.map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/learn/${s.slug}`}
                          className="group block"
                        >
                          <p className="text-sm font-semibold text-foreground group-hover:text-primary leading-snug transition-colors">
                            {s.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {s.readingMinutes} min · {s.category}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      <FadeIn>
        <PhotoSlider
          slotKey={`learn-article-slider:${article.slug}`}
          route={`/learn/${article.slug}`}
          pageLabel={`Article: ${article.slug}`}
          slot="learn-article-slider"
          aspect="4/3"
          perView={{ base: 1, sm: 2, lg: 3 }}
          eyebrow="See it in practice"
          heading="The principles in this guide, on real properties"
          body="Everything we write about comes straight from installs we've completed for homeowners across our service area."
          background="background"
        />
      </FadeIn>

      {/* Related articles strip */}
      <FadeIn>
        <section className="py-14 bg-offwhite border-t border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">More articles</h2>
              <Link to="/learn" className="text-primary font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">
                Browse all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/learn/${a.slug}`}
                    className="group bg-background rounded-lg border border-border hover:border-primary/40 hover:shadow-md transition-all overflow-hidden"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                      <img
                        src={a.heroImage}
                        alt={a.heroAlt}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-primary mb-1">
                        {a.category}
                      </p>
                      <p className="font-semibold text-foreground text-sm leading-snug group-hover:text-primary transition-colors">
                        {a.title}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </FadeIn>
      <FadeIn><PricingEstimator /></FadeIn>
    </>
  );
};

export default LearnArticle;
